import { Paragraph } from '@audio-network/componentlibrary'
import { useTranslation } from 'next-i18next'
import { useEffect, useState } from 'react'

import { clientGetMixes } from '@/client/track/getMixes'
import TrackList from '@/components/shared/TrackList/TrackList'
import TrackListSkeleton from '@/components/shared/TrackList/TrackList.skeleton'
import { MIXES } from '@/constants/trackSources'
import sessionStore from '@/stores/sessionStore'
import { isCatalogueAvailableForTrack } from '@/utils/isCatalogueAvailableForTrack'

import styles from './TrackMixes.module.css'
import { TrackMixesProps } from './TrackMixes.types'

const TrackMixes = ({
	track,
	mixes: mixesFromProps,
	isCatalogueAvailable: isCatalogueAvailableFromProps,
	skipQueueInitialisation,
}: TrackMixesProps): JSX.Element => {
	const { t } = useTranslation(['download'])
	const [mixes, setMixes] = useState<Track[] | null>(mixesFromProps ?? null)
	const user = sessionStore((state) => state.session?.user)
	const trackId = track?.id

	useEffect(() => {
		if (mixesFromProps || trackId === undefined) {
			return
		}

		let isStale = false
		setMixes(null)

		clientGetMixes(trackId).then((fetchedMixes) => {
			if (!isStale) {
				setMixes(fetchedMixes)
			}
		})

		return (): void => {
			isStale = true
		}
	}, [trackId, mixesFromProps])

	if (mixes === null) {
		return <TrackListSkeleton />
	}

	if (mixes.length === 0) {
		return <Paragraph>{t('has-no-mixes')}</Paragraph>
	}

	const mainMix = mixes.find((mix) => mix.mix === 1)

	const isCatalogueAvailable =
		isCatalogueAvailableFromProps ?? isCatalogueAvailableForTrack(user, mainMix)

	const downloadName = mainMix?.title ?? 'Mixes'

	return (
		<div className={styles.container}>
			<TrackList
				download={{
					canDownloadAll: true,
					name: downloadName,
				}}
				isCatalogueAvailable={isCatalogueAvailable}
				skipQueueInitialisation={skipQueueInitialisation}
				source={MIXES}
				tracks={mixes}
			/>
		</div>
	)
}

export default TrackMixes
